// src/lib/etiquetasUc.ts
//
// Etiquetas de UC: o faturamento cria (texto, cor, explicação) e aplica em UCs pela lista
// de Faturas e Cobranças e pela lista de UCs
// (docs/superpowers/specs/2026-10-13-etiquetas-uc-design.md).
//
// Tabelas: `etiquetas` (catálogo) e `uc_etiquetas` (vínculo). O vínculo é com a UC, não com
// o número — por isso a etiqueta vale para os dois números da UC sem esforço.

/** Mesmas chaves do CHECK de `etiquetas.cor` no banco: mudou aqui, muda lá. */
export const CORES_ETIQUETA = ['fucsia', 'vermelho', 'verde', 'azul', 'roxo', 'ciano', 'rosa', 'cinza'] as const;
export type CorEtiqueta = typeof CORES_ETIQUETA[number];

export const TEXTO_MAX = 30;
export const DESCRICAO_MAX = 200;

/** Valor do filtro "com qualquer etiqueta". Os outros valores são o id da etiqueta. */
export const FILTRO_QUALQUER_ETIQUETA = '__qualquer__';

export type Etiqueta = {
  id: string;
  texto: string;
  cor: CorEtiqueta;
  descricao: string | null;
};

export type VinculoEtiqueta = {
  uc_id: number;
  etiqueta_id: string;
  aplicada_em: string;
};

export type DadosEtiqueta = Pick<Etiqueta, 'texto' | 'cor' | 'descricao'>;

/**
 * Classes por cor, escritas por extenso: o Tailwind do CDN só gera a classe que enxerga
 * inteira. Âmbar e laranja ficam de fora — são "Teste" e "Parceira" na lista de faturas.
 */
export const ESTILO_COR: Record<CorEtiqueta, { nome: string; pilula: string; contorno: string; fundo: string }> = {
  fucsia: { nome: 'Fúcsia', pilula: 'bg-fuchsia-600 dark:bg-fuchsia-500', contorno: 'ring-2 ring-fuchsia-500 dark:ring-fuchsia-400', fundo: 'bg-fuchsia-50/70 dark:bg-fuchsia-900/10' },
  vermelho: { nome: 'Vermelho', pilula: 'bg-red-600 dark:bg-red-500', contorno: 'ring-2 ring-red-500 dark:ring-red-400', fundo: 'bg-red-50/70 dark:bg-red-900/10' },
  verde: { nome: 'Verde', pilula: 'bg-emerald-600 dark:bg-emerald-500', contorno: 'ring-2 ring-emerald-500 dark:ring-emerald-400', fundo: 'bg-emerald-50/70 dark:bg-emerald-900/10' },
  azul: { nome: 'Azul', pilula: 'bg-blue-600 dark:bg-blue-500', contorno: 'ring-2 ring-blue-500 dark:ring-blue-400', fundo: 'bg-blue-50/70 dark:bg-blue-900/10' },
  roxo: { nome: 'Roxo', pilula: 'bg-violet-600 dark:bg-violet-500', contorno: 'ring-2 ring-violet-500 dark:ring-violet-400', fundo: 'bg-violet-50/70 dark:bg-violet-900/10' },
  ciano: { nome: 'Ciano', pilula: 'bg-cyan-700 dark:bg-cyan-600', contorno: 'ring-2 ring-cyan-500 dark:ring-cyan-400', fundo: 'bg-cyan-50/70 dark:bg-cyan-900/10' },
  rosa: { nome: 'Rosa', pilula: 'bg-pink-600 dark:bg-pink-500', contorno: 'ring-2 ring-pink-500 dark:ring-pink-400', fundo: 'bg-pink-50/70 dark:bg-pink-900/10' },
  cinza: { nome: 'Cinza', pilula: 'bg-slate-600 dark:bg-slate-500', contorno: 'ring-2 ring-slate-500 dark:ring-slate-400', fundo: 'bg-slate-100/70 dark:bg-slate-800/30' },
};

/** Cor desconhecida (gravada por fora da tela) cai no cinza em vez de sumir. */
export function estiloDaCor(cor: string) {
  return ESTILO_COR[cor as CorEtiqueta] ?? ESTILO_COR.cinza;
}

/** Texto comparável: sem espaço nas pontas e sem diferença de caixa — igual ao índice único do banco. */
function chaveTexto(texto: string) {
  return texto.trim().toLowerCase();
}

/**
 * Motivo para não salvar, ou `null` se está tudo certo. `idEditando` deixa a própria
 * etiqueta manter o texto que já tem.
 */
export function validarEtiqueta(dados: DadosEtiqueta, existentes: Etiqueta[], idEditando?: string): string | null {
  const texto = dados.texto.trim();
  if (!texto) return 'Escreva o texto da etiqueta.';
  if (texto.length > TEXTO_MAX) return `O texto passa de ${TEXTO_MAX} letras.`;
  if (!CORES_ETIQUETA.includes(dados.cor)) return 'Escolha uma cor.';
  if ((dados.descricao || '').trim().length > DESCRICAO_MAX) return `A explicação passa de ${DESCRICAO_MAX} letras.`;
  const repetida = existentes.find(e => e.id !== idEditando && chaveTexto(e.texto) === chaveTexto(texto));
  if (repetida) return `Já existe a etiqueta "${repetida.texto}".`;
  return null;
}

/** Dados prontos para gravar: texto aparado e explicação vazia vira `null`. */
export function limparDadosEtiqueta(dados: DadosEtiqueta): DadosEtiqueta {
  const descricao = (dados.descricao || '').trim();
  return { texto: dados.texto.trim(), cor: dados.cor, descricao: descricao || null };
}

/**
 * UC → etiquetas, na ordem em que foram colocadas (a mais antiga primeiro). A primeira dá a
 * cor do contorno do card, então a ordem precisa ser estável. Vínculo de etiqueta que não
 * está no catálogo é ignorado.
 */
export function indexarEtiquetasPorUc(etiquetas: Etiqueta[], vinculos: VinculoEtiqueta[]): Map<number, Etiqueta[]> {
  const porId = new Map(etiquetas.map(e => [e.id, e]));
  const ordenados = [...vinculos].sort((a, b) =>
    a.aplicada_em < b.aplicada_em ? -1 : a.aplicada_em > b.aplicada_em ? 1 : a.etiqueta_id.localeCompare(b.etiqueta_id));
  const indice = new Map<number, Etiqueta[]>();
  for (const v of ordenados) {
    const etiqueta = porId.get(v.etiqueta_id);
    if (!etiqueta) continue;
    const lista = indice.get(v.uc_id);
    if (lista) lista.push(etiqueta);
    else indice.set(v.uc_id, [etiqueta]);
  }
  return indice;
}

/** Em quantas UCs cada etiqueta está — vai no aviso de apagar. */
export function contarUcsPorEtiqueta(vinculos: VinculoEtiqueta[]): Map<string, number> {
  const contagem = new Map<string, number>();
  for (const v of vinculos) contagem.set(v.etiqueta_id, (contagem.get(v.etiqueta_id) ?? 0) + 1);
  return contagem;
}

/** Filtro da lista: '' = todas; FILTRO_QUALQUER_ETIQUETA = tem alguma; senão, o id da etiqueta. */
export function passaNoFiltroDeEtiqueta(etiquetasDaUc: Etiqueta[] | undefined, filtro: string): boolean {
  if (!filtro) return true;
  if (filtro === FILTRO_QUALQUER_ETIQUETA) return (etiquetasDaUc?.length ?? 0) > 0;
  return !!etiquetasDaUc?.some(e => e.id === filtro);
}
